import { X } from 'lucide-react';
import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Modal dialog on the native <dialog> element: focus trap, Escape to close and backdrop come from the browser.
 * When `onSubmit` is given the body is a form, so Enter submits.
 */
export function Dialog({
  open,
  title,
  onClose,
  onSubmit,
  children,
  footer,
  wide,
  testId,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit?: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  testId?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  const body = (
    <>
      <div className="dialog-head">
        <h2>{title}</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
          <X size={16} />
        </button>
      </div>
      <div className="dialog-body">{children}</div>
      {footer && <div className="dialog-foot">{footer}</div>}
    </>
  );
  return (
    <dialog
      ref={ref}
      className={`dialog${wide ? ' dialog-wide' : ''}`}
      onClose={onClose}
      onCancel={(e) => {
        // Escape inside an open dropdown or date picker closes only that, not the whole dialog.
        if (ref.current?.querySelector('.picker-pop')) e.preventDefault();
      }}
      data-testid={testId}
      aria-label={title}
    >
      {open &&
        (onSubmit ? (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              // A dialog opened from inside another dialog's form must not submit that form too.
              e.stopPropagation();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          body
        ))}
    </dialog>
  );
}
